import React, { useState, type FormEvent } from "react";
import type { PasswordCredentials } from "../types";

export interface LoginFormProps {
  onSubmit: (credentials: PasswordCredentials) => void | Promise<void>;
  loading?: boolean;
  error?: string | null;
  className?: string;
  identifierLabel?: string;
  showRememberMe?: boolean;
}

interface FieldErrors {
  identifier?: string;
  password?: string;
}

function validate(identifier: string, password: string): FieldErrors {
  const errors: FieldErrors = {};
  if (!identifier.trim()) errors.identifier = "Enter your email or username.";
  if (!password) errors.password = "Enter your password.";
  return errors;
}

/** Headless-friendly login form. Bring your own container via `className`; every element carries a stable `fka-*` class for restyling. */
export function LoginForm({
  onSubmit,
  loading = false,
  error,
  className,
  identifierLabel = "Email or username",
  showRememberMe = true,
}: LoginFormProps) {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errors = validate(identifier, password);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    await onSubmit({ identifier, password, rememberMe });
  };

  return (
    <form className={className ?? "fka-login-form flex flex-col gap-4"} onSubmit={handleSubmit} noValidate>
      <div className="fka-field flex flex-col gap-1">
        <label htmlFor="fka-identifier" className="fka-label text-sm font-medium text-slate-300">
          {identifierLabel}
        </label>
        <input
          id="fka-identifier"
          name="identifier"
          type="text"
          autoComplete="username"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          aria-invalid={Boolean(fieldErrors.identifier)}
          aria-describedby={fieldErrors.identifier ? "fka-identifier-error" : undefined}
          className="fka-input rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100 outline-none focus:border-amber-500"
        />
        {fieldErrors.identifier && (
          <p id="fka-identifier-error" className="fka-field-error text-sm text-red-400">
            {fieldErrors.identifier}
          </p>
        )}
      </div>

      <div className="fka-field flex flex-col gap-1">
        <label htmlFor="fka-password" className="fka-label text-sm font-medium text-slate-300">
          Password
        </label>
        <input
          id="fka-password"
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          aria-invalid={Boolean(fieldErrors.password)}
          aria-describedby={fieldErrors.password ? "fka-password-error" : undefined}
          className="fka-input rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100 outline-none focus:border-amber-500"
        />
        {fieldErrors.password && (
          <p id="fka-password-error" className="fka-field-error text-sm text-red-400">
            {fieldErrors.password}
          </p>
        )}
      </div>

      {showRememberMe && (
        <label className="fka-remember-me flex items-center gap-2 text-sm text-slate-400">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="fka-checkbox"
          />
          Remember me
        </label>
      )}

      {error && (
        <p role="alert" className="fka-server-error text-sm text-red-400">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="fka-submit rounded-md bg-amber-500 px-4 py-2 font-medium text-slate-950 transition disabled:cursor-not-allowed disabled:opacity-60 hover:bg-amber-400"
      >
        {loading ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
}
